import { useTranslation } from 'react-i18next';
import { RadioButton } from '@spotted-zebra-uk/ui-components';
import { GrammaticalGender, SupportedLocale } from '../../../generated/graphql';
import styles from './GrammaticalGenderSelection.module.scss';

type GenderedExample = {
  tag: string;
  lang: string;
  [GrammaticalGender.Masculine]: string;
  [GrammaticalGender.Feminine]: string;
};

// Examples stay in the interview language regardless of the UI locale. They
// are shown on their own: the masculine/feminine pair already makes the
// difference visible, and a translated gloss only repeated it when the UI
// and interview languages matched (CR-493).
const GENDERED_EXAMPLES: Partial<Record<SupportedLocale, GenderedExample>> = {
  [SupportedLocale.Polish]: {
    tag: 'PL',
    lang: 'pl',
    [GrammaticalGender.Masculine]: 'opowiedziałeś',
    [GrammaticalGender.Feminine]: 'opowiedziałaś',
  },
  [SupportedLocale.French]: {
    tag: 'FR',
    lang: 'fr',
    [GrammaticalGender.Masculine]: 'tu es allé',
    [GrammaticalGender.Feminine]: 'tu es allée',
  },
  [SupportedLocale.SpanishLatinAmerican]: {
    tag: 'ES',
    lang: 'es',
    [GrammaticalGender.Masculine]: 'estás preparado',
    [GrammaticalGender.Feminine]: 'estás preparada',
  },
  [SupportedLocale.SpanishEuropean]: {
    tag: 'ES',
    lang: 'es',
    [GrammaticalGender.Masculine]: 'estás preparado',
    [GrammaticalGender.Feminine]: 'estás preparada',
  },
  [SupportedLocale.Italian]: {
    tag: 'IT',
    lang: 'it',
    [GrammaticalGender.Masculine]: 'sei stato',
    [GrammaticalGender.Feminine]: 'sei stata',
  },
  [SupportedLocale.Bulgarian]: {
    tag: 'BG',
    lang: 'bg',
    [GrammaticalGender.Masculine]: 'ти си готов',
    [GrammaticalGender.Feminine]: 'ти си готова',
  },
  [SupportedLocale.PortugueseBrazilian]: {
    tag: 'PT',
    lang: 'pt-BR',
    [GrammaticalGender.Masculine]: 'você está preparado',
    [GrammaticalGender.Feminine]: 'você está preparada',
  },
};

const OPTIONS = [
  { value: GrammaticalGender.Masculine, labelKey: 'masculine' },
  { value: GrammaticalGender.Feminine, labelKey: 'feminine' },
] as const;

interface GrammaticalGenderSelectionProps {
  locale: SupportedLocale;
  value: GrammaticalGender;
  onChange: (value: GrammaticalGender) => void;
}

export function GrammaticalGenderSelection({
  locale,
  value,
  onChange,
}: GrammaticalGenderSelectionProps) {
  const { t } = useTranslation();
  const example = GENDERED_EXAMPLES[locale];

  return (
    <section
      className={styles.section}
      data-testid="ai-interview-grammatical-gender"
    >
      <h2 className={styles.title}>
        {t('aiInterview.grammaticalGender.title')}
      </h2>
      <p className={styles.body}>{t('aiInterview.grammaticalGender.body')}</p>
      <fieldset className={styles.fieldset}>
        <legend className={styles.question}>
          {t('aiInterview.grammaticalGender.question')}
        </legend>
        {OPTIONS.map(option => (
          <RadioButton
            key={option.value}
            id={`ai-interview-grammatical-gender-${option.value}`}
            name="ai-interview-grammatical-gender"
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            className={styles.option}
          >
            <span className={styles.optionContent}>
              <span className={styles.optionLabel}>
                {t(`aiInterview.grammaticalGender.${option.labelKey}`)}
              </span>
              {example && (
                <span className={styles.example}>
                  <span className={styles.exampleTag}>{example.tag}</span>{' '}
                  <span lang={example.lang}>
                    &ldquo;{example[option.value]}&rdquo;
                  </span>
                </span>
              )}
            </span>
          </RadioButton>
        ))}
      </fieldset>
    </section>
  );
}
